import type { GetDeteriorationModelAssetsResultsPaginatedRequests } from '@Types/deteriorationModels.types';

type AssetsResultsRequest = GetDeteriorationModelAssetsResultsPaginatedRequests;

/** What the store knows about the asset results it holds. */
export interface StoredAssetsResults {
	isLoading: boolean;
	request?: AssetsResultsRequest | null;
}

/**
 * True when two requests would return the same page of results. `filterModel` is
 * left out: it is the grid's UI state, and `filter` is what reaches the API.
 */
export const isSameAssetsResultsQuery = (a: AssetsResultsRequest, b: AssetsResultsRequest): boolean =>
	a.modelId === b.modelId &&
	a.runId === b.runId &&
	a.page === b.page &&
	a.slice === b.slice &&
	a.sortKey === b.sortKey &&
	a.sortDescending === b.sortDescending &&
	a.searchValue === b.searchValue &&
	JSON.stringify(a.filter ?? null) === JSON.stringify(b.filter ?? null);

/**
 * True when the store already holds the results for `request` from the model's
 * current run, so fetching again would only redraw the same rows. Checking the run
 * separately matters: after a re-run the component re-sends a request that still
 * carries the run id it was created with, and that must not count as cached.
 */
export const hasStoredAssetsResults = (
	stored: StoredAssetsResults,
	request: AssetsResultsRequest,
	currentRunId: string | null | undefined,
): boolean =>
	!stored.isLoading &&
	!!stored.request &&
	stored.request.runId === currentRunId &&
	isSameAssetsResultsQuery(stored.request, request);

/**
 * True when results for this model's current run have been requested, whether they
 * are loaded or still in flight. A failed request clears the stored request, so a
 * failure is never treated as having results.
 */
export const hasRequestedAssetsResultsForRun = (
	stored: StoredAssetsResults,
	modelId: string,
	currentRunId: string | null | undefined,
): boolean => !!stored.request && stored.request.modelId === modelId && stored.request.runId === currentRunId;
